import {
  createContext,
  useContext,
  useId,
  useMemo,
  type ReactNode,
} from "react";
import clsx from "clsx";

import styles from "./accordion.module.css";

function accordionIdFragment(v: string): string {
  const s = v
    .replace(/[^a-zA-Z0-9_-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return s.length > 0 ? s : "item";
}

type AccordionContextValue =
  | {
      mode: "single";
      idPrefix: string;
      value: string | null;
      onValueChange: (value: string | null) => void;
      collapsible: boolean;
    }
  | {
      mode: "multiple";
      idPrefix: string;
      value: string[];
      onValueChange: (value: string[]) => void;
    };

const AccordionContext = createContext<AccordionContextValue | null>(null);

function useAccordionContext(component: string): AccordionContextValue {
  const ctx = useContext(AccordionContext);
  if (ctx == null) {
    throw new Error(`${component} must be used within <Accordion>`);
  }
  return ctx;
}

type AccordionItemContextValue = { value: string };

const AccordionItemContext = createContext<AccordionItemContextValue | null>(
  null,
);

function useAccordionItemContext(component: string): AccordionItemContextValue {
  const ctx = useContext(AccordionItemContext);
  if (ctx == null) {
    throw new Error(`${component} must be used within <AccordionItem>`);
  }
  return ctx;
}

function isItemOpen(ctx: AccordionContextValue, itemValue: string): boolean {
  if (ctx.mode === "single") {
    return ctx.value === itemValue;
  }
  return ctx.value.includes(itemValue);
}

function toggleItem(ctx: AccordionContextValue, itemValue: string): void {
  if (ctx.mode === "single") {
    const { value, onValueChange, collapsible } = ctx;
    if (value === itemValue) {
      if (collapsible) {
        onValueChange(null);
      }
    } else {
      onValueChange(itemValue);
    }
    return;
  }

  const { value, onValueChange } = ctx;
  if (value.includes(itemValue)) {
    onValueChange(value.filter((v) => v !== itemValue));
  } else {
    onValueChange([...value, itemValue]);
  }
}

export type AccordionBaseProps = {
  children: ReactNode;
  className?: string;
  /** Base string for stable `id` / `aria-*` wiring; defaults to a React `useId()`-derived prefix. */
  idPrefix?: string;
};

export type AccordionSingleProps = AccordionBaseProps & {
  type?: "single";
  /** Currently open item value, or `null` when all closed (only if `collapsible` is true). */
  value: string | null;
  onValueChange: (value: string | null) => void;
  /**
   * When `true`, the open item can be closed so that no item is open.
   * @default false
   */
  collapsible?: boolean;
};

export type AccordionMultipleProps = AccordionBaseProps & {
  type: "multiple";
  value: string[];
  onValueChange: (value: string[]) => void;
};

export type AccordionProps = AccordionSingleProps | AccordionMultipleProps;

export function Accordion(props: AccordionProps) {
  const reactId = useId();
  const resolvedPrefix = props.idPrefix ?? `accordion-${reactId.replace(/:/g, "")}`;

  const ctx = useMemo<AccordionContextValue>(() => {
    if (props.type === "multiple") {
      return {
        mode: "multiple",
        idPrefix: resolvedPrefix,
        value: props.value,
        onValueChange: props.onValueChange,
      };
    }
    return {
      mode: "single",
      idPrefix: resolvedPrefix,
      value: props.value,
      onValueChange: props.onValueChange,
      collapsible: props.collapsible ?? false,
    };
  }, [props, resolvedPrefix]);

  return (
    <AccordionContext.Provider value={ctx}>
      <div className={clsx(styles.root, props.className)}>{props.children}</div>
    </AccordionContext.Provider>
  );
}

export interface AccordionItemProps {
  /** Unique id for this section (must be unique within the accordion). */
  value: string;
  children: ReactNode;
  className?: string;
}

export function AccordionItem({ value, children, className }: AccordionItemProps) {
  const ctx = useAccordionContext("AccordionItem");
  const open = isItemOpen(ctx, value);

  const itemCtx = useMemo<AccordionItemContextValue>(
    () => ({ value }),
    [value],
  );

  return (
    <AccordionItemContext.Provider value={itemCtx}>
      <div
        className={clsx(styles.item, className)}
        data-state={open ? "open" : "closed"}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

export interface AccordionHeaderProps {
  children: ReactNode;
  className?: string;
}

export function AccordionHeader({ children, className }: AccordionHeaderProps) {
  return <div className={clsx(styles.header, className)}>{children}</div>;
}

export interface AccordionTriggerProps {
  children: ReactNode;
  className?: string;
}

export function AccordionTrigger({ children, className }: AccordionTriggerProps) {
  const ctx = useAccordionContext("AccordionTrigger");
  const { value: itemValue } = useAccordionItemContext("AccordionTrigger");
  const open = isItemOpen(ctx, itemValue);
  const fragment = accordionIdFragment(itemValue);
  const triggerId = `${ctx.idPrefix}-trigger-${fragment}`;
  const panelId = `${ctx.idPrefix}-panel-${fragment}`;

  return (
    <button
      type="button"
      id={triggerId}
      className={clsx(styles.trigger, className)}
      aria-expanded={open}
      aria-controls={panelId}
      data-state={open ? "open" : "closed"}
      onClick={() => {
        toggleItem(ctx, itemValue);
      }}
    >
      {children}
    </button>
  );
}

export interface AccordionContentProps {
  children: ReactNode;
  className?: string;
}

export function AccordionContent({ children, className }: AccordionContentProps) {
  const ctx = useAccordionContext("AccordionContent");
  const { value: itemValue } = useAccordionItemContext("AccordionContent");
  const open = isItemOpen(ctx, itemValue);
  const fragment = accordionIdFragment(itemValue);
  const triggerId = `${ctx.idPrefix}-trigger-${fragment}`;
  const panelId = `${ctx.idPrefix}-panel-${fragment}`;

  return (
    <div
      id={panelId}
      className={clsx(styles.content, className)}
      role="region"
      aria-labelledby={triggerId}
      hidden={!open}
      data-state={open ? "open" : "closed"}
    >
      {children}
    </div>
  );
}
